import { memo, useMemo, useRef } from 'react'
import { useWatch } from 'react-hook-form'

import BlockIcon from '@mui/icons-material/Block'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'

import { FormField } from '~/common/components'
import { useLanguage } from '~/common/hooks'
import type { UserLocation } from '~/common/schemas/location'
import Section from '~/components/Section'
import { useProductPage } from '~/products/hooks/useProductPage'
import { createDimensionsColumns } from '~/products/utils/createDimensionColumns'
import {
    type ProductFormSchema,
    type StockUnitFormSchema,
    useProductFormContext,
} from '../../hooks/useProductForm'

type StockRecordsTableProps = {
    stockUnits: ProductFormSchema['stock_units']
    dimensions: ProductFormSchema['dimensions']
    locations: UserLocation[]
}

function StockRecordsTable({ stockUnits, dimensions, locations }: StockRecordsTableProps) {
    const language = useLanguage()

    const columns = useMemo(() => {
        const columnHelper = createColumnHelper<StockUnitFormSchema>()

        return [
            columnHelper.accessor('sku', {
                header: 'SKU',
                cell: ({ row }) => {
                    return row.original.sku || interpolate(gettext('SKU %s'), [row.index + 1])
                },
            }),
            ...createDimensionsColumns(dimensions, language),
            ...locations.map((location) => {
                return columnHelper.accessor(
                    (row) => {
                        return row.stock_records.findIndex(
                            (record) => record.location.location_id === location.location_id
                        )
                    },
                    {
                        id: location.location_id.toString(),
                        header: location.name,
                        cell: ({ row, getValue }) => {
                            const stockRecordIndex = getValue()
                            const stockUnitIndex = row.index
                            const isBundle = row.original.stock_item.is_bundle

                            if (isBundle) {
                                return (
                                    <Tooltip
                                        title={gettext('Cannot edit stock for this stock unit')}
                                    >
                                        <BlockIcon color="disabled" fontSize="small" />
                                    </Tooltip>
                                )
                            }

                            return (
                                <FormField
                                    fullWidth
                                    type="number"
                                    name={`stock_units.${stockUnitIndex}.stock_records.${stockRecordIndex}.quantity`}
                                    inputProps={{ min: 0 }}
                                />
                            )
                        },
                        size: 200,
                    }
                )
            }),
        ]
    }, [dimensions, locations, language])

    const table = useReactTable({
        data: stockUnits,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => row.id.toString(),
    })

    const rows = table.getRowModel().rows
    const tableContainerRef = useRef<HTMLDivElement>(null)

    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        estimateSize: () => 65,
        getItemKey: (index) => rows[index]?.id ?? index,
        getScrollElement: () => tableContainerRef.current,
        //measure dynamic row height, except in firefox because it measures table border height incorrectly
        measureElement:
            typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
                ? (element) => element?.getBoundingClientRect().height
                : undefined,
    })

    return (
        <TableContainer
            sx={{ maxHeight: 320, overflowY: 'auto', position: 'relative' }}
            ref={tableContainerRef}
        >
            <Table size="small" sx={{ width: 'auto', display: 'grid' }}>
                <TableHead
                    sx={{
                        display: 'grid',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        backgroundColor: (theme) => theme.palette.background.paper,
                    }}
                >
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow
                            key={headerGroup.id}
                            sx={{ display: 'flex', width: '100%', height: 'auto' }}
                        >
                            {headerGroup.headers.map((header) => (
                                <TableCell
                                    key={header.id}
                                    sx={{ width: header.getSize(), display: 'flex' }}
                                >
                                    {flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableHead>
                <TableBody
                    sx={{
                        display: 'grid',
                        height: rowVirtualizer.getTotalSize(),
                        position: 'relative',
                    }}
                >
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                        const row = rows[virtualRow.index]
                        if (!row) {
                            return null
                        }

                        return (
                            <TableRow
                                key={row.id}
                                data-index={virtualRow.index}
                                ref={(node) => rowVirtualizer.measureElement(node)}
                                sx={{ width: '100%', position: 'absolute', display: 'flex' }}
                                style={{
                                    transform: `translateY(${virtualRow.start}px)`,
                                }}
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <TableCell
                                        key={cell.id}
                                        sx={{
                                            width: cell.column.getSize(),
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default function StockRecords() {
    const { productInfo } = useProductPage()
    const { control } = useProductFormContext()
    const stockUnits = useWatch({ control, name: 'stock_units' })
    const dimensions = useWatch({ control, name: 'dimensions', exact: true, defaultValue: [] })

    return (
        <Section header={<Section.Header label={gettext('Stock')} />}>
            <StockRecordsTable
                stockUnits={stockUnits}
                dimensions={dimensions}
                locations={productInfo.locations}
            />
        </Section>
    )
}
